import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FolderLock, Upload, FileCheck, Download, Clock, CheckCircle2,
  ShieldCheck, FileText, AlertCircle, Loader2
} from "lucide-react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { Button } from "../components/ui/button";
import { CLIENT_DOCS, DRIVER_DOCS } from "../data/compliance";
import { uploadComplianceDoc, acknowledgeDoc, expiryState } from "../lib/compliance";

const STATUS = {
  pending: { label: "Pending review", cls: "bg-amber-100 text-amber-800" },
  approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rejected", cls: "bg-red-100 text-red-700" },
  acknowledged: { label: "Acknowledged", cls: "bg-sky-100 text-sky-700" }
};

export default function ComplianceCenter() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [docs, setDocs] = useState([]);
  const [busyKey, setBusyKey] = useState("");
  const fileRefs = useRef({});

  const isDriver = user?.role === "driver";
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (!user) return;
    // Drivers see their own docs; clients/admins see shared client docs.
    const q = isDriver
      ? query(collection(db, "compliance_documents"), where("ownerUid", "==", user.id))
      : query(collection(db, "compliance_documents"), where("category", "==", "client"));
    const unsub = onSnapshot(
      q,
      (snap) => setDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.warn("compliance docs read error:", err?.message)
    );
    return () => unsub();
  }, [user, isDriver]);

  const byKey = useMemo(() => {
    const m = {};
    docs.forEach((d) => {
      const cur = m[d.docKey];
      const t = d.createdAt?.seconds || 0;
      if (!cur || t >= (cur._t || 0)) m[d.docKey] = { ...d, _t: t };
    });
    return m;
  }, [docs]);

  const onDriverUpload = async (docDef, file) => {
    if (!file) return;
    setBusyKey(docDef.key);
    try {
      await uploadComplianceDoc({
        file,
        category: "driver",
        docKey: docDef.key,
        label: docDef.label,
        ownerUid: user.id,
        ownerName: user.name || user.email,
        uploadedBy: user.id,
        uploadedByRole: "driver",
        status: "pending"
      });
      toast({ title: "Uploaded", description: `${docDef.label} submitted for review.` });
    } catch (err) {
      toast({ title: "Upload failed", description: err?.message || "Please try again." });
    } finally {
      setBusyKey("");
    }
  };

  const onAcknowledge = async (docDef) => {
    setBusyKey(docDef.key);
    try {
      await acknowledgeDoc({ docKey: docDef.key, label: docDef.label, ownerUid: user.id, ownerName: user.name || user.email, uploadedBy: user.id });
      toast({ title: "Acknowledged", description: `${docDef.label} recorded.` });
    } catch (err) {
      toast({ title: "Couldn't record", description: err?.message });
    } finally {
      setBusyKey("");
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      <section className="bg-[#0f2847] text-white stripe-bg">
        <div className="container-518 py-10">
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#f59321] mb-1">Compliance Document Center</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold flex items-center gap-3">
            <FolderLock className="w-8 h-8 text-[#f59321]" /> Secure documents
          </h1>
          <p className="mt-2 text-slate-300 max-w-2xl">
            {isDriver
              ? "Upload your certificates and training records, acknowledge SOPs, and track expiration dates. Administrators review and approve submissions."
              : "Compliance documentation for qualified clients and partners. Approved documents can be downloaded below; anything not shown is available on request during onboarding."}
          </p>
        </div>
      </section>

      <section className="container-518 py-8">
        {isAdmin && (
          <div className="mb-6 flex items-start gap-3 p-4 rounded-2xl bg-white border border-slate-200" data-testid="cc-admin-note">
            <ShieldCheck className="w-5 h-5 text-[#f59321] mt-0.5 shrink-0" />
            <div className="text-sm text-slate-600">
              You are viewing the shared client documents. To manage registrations, approve driver documents, track expirations and export reports, open the{" "}
              <Link to="/admin-dashboard" className="text-[#f59321] font-semibold underline">Admin dashboard → Compliance</Link> tab.
            </div>
          </div>
        )}

        {/* DRIVER VIEW */}
        {isDriver ? (
          <div className="space-y-4" data-testid="cc-driver-docs">
            {DRIVER_DOCS.map((d) => {
              const cur = byKey[d.key];
              const st = cur ? STATUS[cur.status] || STATUS.pending : null;
              const exp = expiryState(cur?.expiresAt);
              const isAck = d.key === "sop-acknowledgement";
              return (
                <div key={d.key} className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-wrap items-center gap-4" data-testid={`cc-doc-${d.key}`}>
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-display font-bold text-[#0f2847]">{d.label}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                      {st ? (
                        <span className={`px-2 py-0.5 rounded-full font-bold ${st.cls}`}>{st.label}</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full font-bold bg-slate-100 text-slate-500">Not submitted</span>
                      )}
                      {cur && d.hasExpiry && (
                        <span className={`font-semibold ${exp.cls}`}><Clock className="w-3 h-3 inline -mt-0.5 mr-1" />{exp.label}</span>
                      )}
                      {cur?.notes && <span className="text-slate-500 italic">Note: {cur.notes}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {cur?.fileUrl && (
                      <a href={cur.fileUrl} target="_blank" rel="noreferrer">
                        <Button variant="outline" size="sm" className="rounded-full h-9 px-4 font-semibold border-2"><Download className="w-4 h-4 mr-1.5" /> View</Button>
                      </a>
                    )}
                    {isAck ? (
                      <Button
                        onClick={() => onAcknowledge(d)}
                        disabled={busyKey === d.key || cur?.status === "acknowledged"}
                        data-testid={`cc-ack-${d.key}`}
                        className="bg-[#0f2847] hover:bg-[#1e3a5f] text-white rounded-full h-9 px-4 font-semibold disabled:opacity-60"
                      >
                        {busyKey === d.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1.5" /> {cur?.status === "acknowledged" ? "Acknowledged" : "Acknowledge"}</>}
                      </Button>
                    ) : (
                      <>
                        <input
                          type="file"
                          className="hidden"
                          ref={(el) => (fileRefs.current[d.key] = el)}
                          onChange={(e) => onDriverUpload(d, e.target.files?.[0])}
                        />
                        <Button
                          onClick={() => fileRefs.current[d.key]?.click()}
                          disabled={busyKey === d.key}
                          data-testid={`cc-upload-${d.key}`}
                          className="bg-[#f59321] hover:bg-[#e07f10] text-white rounded-full h-9 px-4 font-semibold disabled:opacity-60"
                        >
                          {busyKey === d.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1.5" /> {cur ? "Replace" : "Upload"}</>}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* CLIENT / ADMIN SHARED VIEW */
          <div className="grid md:grid-cols-2 gap-4" data-testid="cc-client-docs">
            {CLIENT_DOCS.map((d) => {
              const cur = byKey[d.key];
              const available = cur && cur.status === "approved" && cur.fileUrl;
              return (
                <div key={d.key} className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4" data-testid={`cc-client-${d.key}`}>
                  <div className="w-11 h-11 rounded-xl bg-[#0f2847] text-white flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-[#0f2847]">{d.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {available ? "Available to download" : "Available on request during onboarding"}
                    </div>
                  </div>
                  {available ? (
                    <a href={cur.fileUrl} target="_blank" rel="noreferrer">
                      <Button size="sm" className="bg-[#f59321] hover:bg-[#e07f10] text-white rounded-full h-9 px-4 font-semibold"><Download className="w-4 h-4 mr-1.5" /> Download</Button>
                    </a>
                  ) : (
                    <Link to="/contact">
                      <Button variant="outline" size="sm" className="rounded-full h-9 px-4 font-semibold border-2">Request</Button>
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-8 flex items-start gap-3 p-5 rounded-2xl bg-slate-50 border border-slate-200 text-sm text-slate-600">
          <AlertCircle className="w-5 h-5 text-[#f59321] mt-0.5 shrink-0" />
          <span>Documents are stored securely and access is limited to authorized clients, drivers and administrators. For a full compliance pack or Business Associate Agreement, <Link to="/contact" className="text-[#f59321] font-semibold hover:underline">contact us</Link>.</span>
        </div>
      </section>
    </div>
  );
}
